/**
 * WHICH CARDS MUST BE AIMED, AND AT WHAT.
 *
 * 🚨 **31 OF 171 CARDS DID NOTHING WHEN PLAYED, SILENTLY, FOR THE WHOLE LIFE OF THIS CLIENT.**
 * *"Rally: Give target creature you control +1/+0"*, *"Rally: Deal 2 damage to enemy target
 * Creature"*, *"Restore the health of target creature"* - every one of them played as a vanilla body
 * with no effect and no message.
 *
 * The cause is an explicit branch in the engine, not a fizzle
 * (`Revival.Core.decompiled.cs:12220`):
 *
 * ```cs
 * EffectData[] array = source.Where(e => e.TargetMod == PlayerPicked).ToArray();
 * if (array.Any() && TargetResolver.HasManualTargets(info.Executor.RuntimeData.Id)) { ...execute... }
 * ```
 *
 * No manual targets, no execution. And `Command.Execute` only registers them when
 * `Targets.Length > 0` (`:17587`) - while `match.ts` sent `TargetObjectsIds: []` on every single
 * `PlayCardCmd`. **The whole feature was one empty array wide.**
 *
 * ⚠️ **THE ENGINE IS THE AUTHORITY ON LEGALITY, AND THIS FILE IS DELIBERATELY PERMISSIVE.**
 * `IsAllowedTarget` re-checks every target, so offering one the engine refuses costs a refused
 * command. Offering one FEWER than the engine allows hides a legal play from the player, and they
 * have no way to discover it. Where the data is ambiguous, offer it.
 *
 * ⚠️ **AIM IS READ FROM THE EFFECT DATA, NEVER FROM THE CARD'S TEXT.** `EFFECT_AIM` is generated
 * from the served database's own `effects[]` - see `tools/gen_web_effect_keywords.py`. The rules
 * text says "target" in English; only the data says `targetMod: PlayerPicked`.
 */

import { EFFECT_AIM, type EffectAim } from "../effect-keywords.generated";
import { cardRecord } from "./cards";
import type { RuntimeObject } from "./state";

/** The aim this card demands when played, or undefined if it needs none. */
export function aimFor(dataId: string | undefined): EffectAim | undefined {
  const rec = cardRecord(dataId);
  const ids = (rec?.effectsIds ?? []) as string[];
  for (const id of ids) {
    const aim = EFFECT_AIM[id];
    if (aim) return aim;
  }
  return undefined;
}

/** True when playing this card without a target would silently do nothing. */
export function needsTarget(dataId: string | undefined): boolean {
  return aimFor(dataId) !== undefined;
}

/**
 * The objects this card may legally be aimed at, from what is on the table.
 *
 * ⚠️ **`owner` IS RELATIVE TO THE CASTER, AND "None" MEANS EITHER SIDE.** Reading "None" as "nobody"
 * would offer zero targets and reproduce the original bug with a targeting UI bolted on top - the
 * card would still do nothing, and now it would look like the game refusing to let you aim.
 */
export function legalPlayTargets(
  aim: EffectAim,
  selfId: string,
  objects: Iterable<RuntimeObject>,
): RuntimeObject[] {
  const out: RuntimeObject[] = [];
  for (const o of objects) {
    if (o.State !== "InTable") continue;

    // Heroes are only aimable when the effect says so; most "target creature" effects do not.
    const kind = String(o.Type ?? "");
    if (aim.types.length && !aim.types.some((t) => t === kind || t === "None")) continue;

    const mine = o.OwnerUserId === selfId;
    if (aim.owner === "Self" && !mine) continue;
    if (aim.owner === "Opponent" && mine) continue;

    out.push(o);
  }
  return out;
}
